<script setup lang="ts">
/**
 * BannerCompactCta
 *
 * A lightweight horizontal CTA banner designed to maintain page momentum.
 * Opens intent modal for quick enquiries instead of scrolling.
 *
 * Usage:
 * - Mid-page "reminder" CTA (opens modal)
 * - Section break with action
 * - Alternative to full form embeds
 */

import { Phone } from "lucide-vue-next";
import { Button } from "@/components/ui/button";

interface BannerCompactCtaProps {
  title: string;
  subtitle?: string;
  buttonText: string;
  buttonAction?: "scroll-to-hero" | "scroll-to-id" | "link";
  buttonHref?: string;
  scrollTargetId?: string;
  phone?: string;
  phoneLabel?: string;
  sectionBg?: "dark" | "primary" | "slate";
  /** Hero service context, injected by BlockRenderer; routes residential pages to the residential-cctv-quote modal variant */
  service?: string;
}

const props = withDefaults(defineProps<BannerCompactCtaProps>(), {
  subtitle: "",
  buttonAction: "scroll-to-hero",
  buttonHref: "",
  scrollTargetId: "",
  phone: "",
  phoneLabel: "Or call us",
  sectionBg: "dark",
  service: "",
});

const { open: openIntentModal, handleLink, resolveQuoteIntent } = useIntentModal();
const { trackCtaClick, trackPhoneClick } = useAnalytics();

const bgClass = computed(() => {
  switch (props.sectionBg) {
    case "primary":
      return "bg-primary-600";
    case "slate":
      return "bg-slate-800";
    case "dark":
    default:
      return "bg-slate-900";
  }
});

/**
 * Derive intent from button text using keyword matching
 */
function deriveIntentFromButtonText(text: string): string {
  const lower = text.toLowerCase();
  if (lower.includes("consultation")) return "consultation";
  if (lower.includes("assessment")) return "assessment";
  if (lower.includes("support") || lower.includes("ticket")) return "support";
  if (lower.includes("quote")) return "free-quote";
  return "free-quote"; // Default fallback
}

const handleButtonClick = () => {
  // Track CTA click
  trackCtaClick(props.buttonText, 'compact_cta', props.buttonHref || '#');

  switch (props.buttonAction) {
    case "scroll-to-hero":
      // Open intent modal instead of scrolling
      openIntentModal(resolveQuoteIntent(deriveIntentFromButtonText(props.buttonText), props.service));
      break;
    case "scroll-to-id":
      if (props.scrollTargetId) {
        const target = document.getElementById(props.scrollTargetId);
        target?.scrollIntoView({ behavior: "smooth" });
      }
      break;
    case "link":
      if (props.buttonHref) {
        // Check if this is an intent link that should open modal
        if (!handleLink(props.buttonHref)) {
          navigateTo(props.buttonHref);
        }
      }
      break;
  }
};

const handlePhoneClick = () => {
  trackPhoneClick('compact_cta');
};
</script>

<template>
  <section :class="[bgClass, 'py-12 sm:py-16']">
    <div class="mx-auto max-w-7xl px-8">
      <div class="flex flex-col items-center justify-between gap-8 lg:flex-row">
        <!-- Text Content -->
        <div class="text-center lg:text-left">
          <h2 class="text-2xl font-bold tracking-tight text-white sm:text-3xl">
            {{ title }}
          </h2>
          <p v-if="subtitle" class="mt-2 text-lg text-slate-300">
            {{ subtitle }}
          </p>
        </div>

        <!-- Actions -->
        <div class="flex flex-col items-center gap-4 sm:flex-row sm:gap-6">
          <Button
            size="lg"
            class="rounded-full bg-mw-accent px-8 text-base font-semibold text-slate-900 hover:bg-mw-accent-400"
            @click="handleButtonClick"
          >
            {{ buttonText }}
          </Button>

          <!-- Optional Phone. The content `phone` field only toggles visibility;
               the number itself comes from the region resolver so city pages
               stay coupled to the GTM-tracked display string. -->
          <LocalPhone
            v-if="phone"
            class="group flex shrink-0 items-center gap-2 text-slate-300 transition-colors hover:text-white"
            @click="handlePhoneClick"
          >
            <template #default="{ display }">
              <span
                class="flex size-10 items-center justify-center rounded-full bg-slate-800 transition-colors group-hover:bg-slate-700"
              >
                <Phone class="size-5" />
              </span>
              <span class="text-sm whitespace-nowrap">
                <span class="block text-xs uppercase tracking-wide text-slate-400">{{
                  phoneLabel
                }}</span>
                <span class="font-semibold">{{ display }}</span>
              </span>
            </template>
          </LocalPhone>
        </div>
      </div>
    </div>
  </section>
</template>
